import { useEffect, useId, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface PromoBucket {
  id: string;
  minutesTotal: number;
  minutesUsed: number;        // base + new attribution
  minutesRemaining: number;
  reason: string;
  expiresAt: string | null;
}

export interface MentoringWallet {
  hasPlan: boolean;
  planKey: string;
  planMinutes: number;
  planUsed: number;
  rolloverMinutes: number;
  rolloverUsed: number;
  rolloverExpiresAt: string;     // last day of current month (rollover from prev month)
  promos: PromoBucket[];
  extraMinutes: number;          // purchased extra-hours (no expiration)
  extraUsed: number;
  consumedTotal: number;
  totalAvailable: number;
  totalRemaining: number;
  daysToReset: number;
}

const lastDayOfMonth = (y: number, m: number) => {
  // Use local date components to avoid UTC offset rolling back to previous day
  const d = new Date(y, m + 1, 0);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

export function useMentoringWallet(userId?: string | null) {
  const channelInstanceId = useId().replace(/:/g, "");
  const [wallet, setWallet] = useState<MentoringWallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (userId === null) {
        if (!cancelled) { setLoading(false); setWallet(null); }
        return;
      }
      let uid = userId ?? null;
      if (!uid) {
        const { data: { session } } = await supabase.auth.getSession();
        uid = session?.user.id ?? null;
      }
      if (!uid) {
        if (!cancelled) { setLoading(false); setWallet(null); }
        return;
      }

      const { data: plan } = await supabase
        .from("client_plans")
        .select("plan_key, monthly_minutes, access_type, created_at")
        .eq("user_id", uid)
        .neq("access_type", "cancelled")
        .maybeSingle();

      const now = new Date();
      const y = now.getFullYear();
      const m = now.getMonth();
      const currentMonthStart = new Date(y, m, 1).toISOString().slice(0, 10);
      const prevMonthStart = new Date(y, m - 1, 1).toISOString().slice(0, 10);
      const today = now.toISOString().slice(0, 10);

      const [currentRes, prevRes, promoRes, extraRes] = await Promise.all([
        supabase.from("time_entries").select("minutes").eq("user_id", uid).gte("entry_date", currentMonthStart),
        supabase.from("time_entries").select("minutes").eq("user_id", uid).gte("entry_date", prevMonthStart).lt("entry_date", currentMonthStart),
        supabase.from("mentoring_promo_credits")
          .select("id, minutes, consumed_minutes, reason, expires_at")
          .eq("user_id", uid)
          .or(`expires_at.is.null,expires_at.gte.${today}`)
          .order("expires_at", { ascending: true, nullsFirst: false }),
        supabase.from("extra_hours_credits")
          .select("minutes, consumed_minutes")
          .eq("user_id", uid),
      ]);

      const planMonthlyMinutes = plan?.monthly_minutes ?? 0;
      const consumedTotal = (currentRes.data ?? []).reduce((s, e: any) => s + (e.minutes ?? 0), 0);
      const prevConsumed = (prevRes.data ?? []).reduce((s, e: any) => s + (e.minutes ?? 0), 0);
      // Rollover only exists when the plan was already active during the previous month.
      const planStartedBeforeThisMonth =
        !!plan?.created_at && plan.created_at.slice(0, 10) < currentMonthStart;
      const rolloverMinutes = plan && planStartedBeforeThisMonth
        ? Math.max(0, Math.min(planMonthlyMinutes - prevConsumed, planMonthlyMinutes))
        : 0;


      const extraMinutes = (extraRes.data ?? []).reduce(
        (s, c: any) => s + Math.max(0, (c.minutes ?? 0) - (c.consumed_minutes ?? 0)),
        0,
      );

      // Attribution priority: rollover (extra, expires end of month) → plan (current month) → promos (oldest expiry) → extras (purchased, no expiry)
      let toAllocate = consumedTotal;

      const rolloverUsed = Math.min(rolloverMinutes, toAllocate);
      toAllocate -= rolloverUsed;

      const planUsed = Math.min(planMonthlyMinutes, toAllocate);
      toAllocate -= planUsed;

      const promos: PromoBucket[] = (promoRes.data ?? []).map((p: any) => {
        const total = p.minutes ?? 0;
        const baseUsed = p.consumed_minutes ?? 0;
        const available = Math.max(0, total - baseUsed);
        const allocated = Math.min(available, toAllocate);
        toAllocate -= allocated;
        return {
          id: p.id,
          minutesTotal: total,
          minutesUsed: baseUsed + allocated,
          minutesRemaining: available - allocated,
          reason: p.reason || "",
          expiresAt: p.expires_at,
        };
      });

      const extraUsed = Math.min(extraMinutes, toAllocate);

      const promoTotalSum = promos.reduce((s, p) => s + p.minutesTotal, 0);
      const totalAvailable = planMonthlyMinutes + rolloverMinutes + promoTotalSum + extraMinutes;
      const totalRemaining = Math.max(0, totalAvailable - consumedTotal);

      const endOfMonth = new Date(y, m + 1, 0);
      const daysLeft = Math.max(
        0,
        Math.ceil((endOfMonth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      );

      if (cancelled) return;
      setWallet({
        hasPlan: !!plan,
        planKey: plan?.plan_key ?? "",
        planMinutes: planMonthlyMinutes,
        planUsed,
        rolloverMinutes,
        rolloverUsed,
        rolloverExpiresAt: plan ? lastDayOfMonth(y, m) : "",
        promos,
        extraMinutes,
        extraUsed,
        consumedTotal,
        totalAvailable,
        totalRemaining,
        daysToReset: daysLeft,
      });
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [reloadTick, userId]);

  // Realtime: any change on the user's time_entries or vip_session_requests refreshes the wallet
  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    (async () => {
      let uid = userId ?? null;
      if (!uid) {
        const { data } = await supabase.auth.getSession();
        uid = data.session?.user?.id ?? null;
      }
      if (!uid || cancelled) return;
      channel = supabase
        .channel(`mentoring-wallet-${uid}-${channelInstanceId}`)
        .on("postgres_changes", { event: "*", schema: "public", table: "time_entries", filter: `user_id=eq.${uid}` },
          () => setReloadTick((t) => t + 1))
        .on("postgres_changes", { event: "*", schema: "public", table: "vip_session_requests", filter: `user_id=eq.${uid}` },
          () => setReloadTick((t) => t + 1))
        .on("postgres_changes", { event: "*", schema: "public", table: "extra_hours_credits", filter: `user_id=eq.${uid}` },
          () => setReloadTick((t) => t + 1))
        .on("postgres_changes", { event: "*", schema: "public", table: "mentoring_promo_credits", filter: `user_id=eq.${uid}` },
          () => setReloadTick((t) => t + 1))
        .subscribe();
    })();
    return () => {
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, [channelInstanceId, userId]);

  return { wallet, loading, refetch: () => setReloadTick((t) => t + 1) };
}

